import { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useQueryClient } from '@tanstack/react-query';
import { BookMarkedIcon, GoalIcon, MessageSquareIcon, MonitorIcon } from 'lucide-react';

import {
  brainsEndpoints,
  collectionsEndpoints,
  desksEndpoints,
  rulesEndpoints,
} from '@/api/endpoints';
import IfIcon from '@/components/atoms/Icons/IfIcon';
import { useAccount } from '@/hooks/useAccount';
import { type Desks } from '@/hooks/useDesks';
import { type Brains } from '@/shared/types/brain';
import { type Collections } from '@/shared/types/collections';
import { type Desk } from '@/shared/types/desk';
import { type Rule } from '@/shared/types/rules';
import { isUuid } from '@/util/uuid';

import { getBrainIcon } from '../icons';
import { type EntityResult } from '../types';
import { assembleCampaignsFromCache } from '../utils';
import { getDateRange } from './useConversationSearch';

const SESSION_BY_ID_QUERY = gql`
  query SessionById(
    $accountId: uuid
    $sessionIds: _text
    $startDate: timestamp
    $endDate: timestamp
  ) {
    rows: search_sessions(
      args: {
        account_id: $accountId
        session_ids: $sessionIds
        start_time: $startDate
        end_time: $endDate
      }
      limit: 1
    ) {
      session_id
      desk_id
      user_name
      channel_user_id
      start_time
    }
  }
`;

interface SessionRow {
  session_id: string;
  desk_id: string;
  user_name: string | null;
  channel_user_id: string | null;
  start_time: string;
}

type UuidLookupResult = { result: EntityResult | null; isLoading: boolean };

const EMPTY: UuidLookupResult = { result: null, isLoading: false };

/** Looks up a UUID against cached entities and remote sessions. */
export const useUuidLookup = (search: string): UuidLookupResult => {
  const queryClient = useQueryClient();
  const { account } = useAccount();
  const trimmed = search.trim();
  const isValid = isUuid(trimmed);

  const cacheResult = useMemo(() => {
    if (!isValid) {
      return null;
    }

    const uuid = trimmed;

    // Brains
    const brainsData = queryClient.getQueryData<Brains>([brainsEndpoints.brains]);
    const brain = brainsData?.brains?.find((b) => b.brain_id === uuid);
    if (brain) {
      return {
        id: brain.brain_id,
        name: brain.name,
        type: 'ai_agent' as const,
        path: `/ai-agents/${brain.brain_id}/overview`,
        icon: getBrainIcon(brain.brain_type),
        score: 1,
      };
    }

    // Collections
    const collectionsData = queryClient.getQueryData<Collections>([
      collectionsEndpoints.collections,
    ]);
    const collection = collectionsData?.collections?.find((c) => c.collection_id === uuid);
    if (collection) {
      return {
        id: collection.collection_id,
        name: collection.name,
        type: 'knowledge_base' as const,
        path: `/knowledge-bases/${collection.collection_id}/category/all`,
        icon: BookMarkedIcon,
        score: 1,
      };
    }

    // Desks
    const desksData = queryClient.getQueryData<Desks>([desksEndpoints.desks]);
    const desk = desksData?.desks?.find((d) => d.desk_id === uuid);
    if (desk) {
      return {
        id: desk.desk_id,
        name: desk.name,
        type: 'environment' as const,
        path: desk.is_service_desk
          ? `/chats/${desk.desk_id}/conversations`
          : `/environments/${desk.desk_id}/integrations`,
        icon: MonitorIcon,
        score: 1,
      };
    }

    // Campaigns
    const campaigns = assembleCampaignsFromCache(queryClient);
    const campaign = campaigns.find((c) => c.campaign_id === uuid);
    if (campaign) {
      const typePath = campaign.type === 'payment_recovery' ? 'payment-recovery' : 'outreach';
      return {
        id: campaign.campaign_id,
        name: campaign.name,
        type: 'campaign' as const,
        path: `/campaigns/${campaign.campaign_id}/${typePath}/setup`,
        icon: GoalIcon,
        score: 1,
      };
    }

    // Rules (nested per desk)
    if (desksData?.desks) {
      for (const d of desksData.desks) {
        const cachedRules = queryClient.getQueryData<{ rules: Rule[] }>([
          rulesEndpoints.rules(d.desk_id),
        ]);
        const rule = cachedRules?.rules?.find((r) => r.rule_id === uuid);
        if (rule) {
          return {
            id: rule.rule_id,
            name: rule.name,
            type: 'rule' as const,
            path: `/environments/${rule.desk_id}/rules/${rule.rule_id}`,
            icon: IfIcon,
            score: 1,
          };
        }
      }
    }

    return null;
  }, [isValid, trimmed, queryClient]);

  // Session lookup via GraphQL — skipped when not a UUID or cache already matched.
  // Use a wider window (90 days) since this is an exact ID lookup, not a fuzzy search.
  const dateRange = useMemo(() => getDateRange(90), []);

  const skipSessionQuery = !isValid || !!cacheResult || !account?.account_id;

  const { data: sessionData, loading: sessionLoading } = useQuery<{ rows: SessionRow[] }>(
    SESSION_BY_ID_QUERY,
    {
      variables: { accountId: account?.account_id, sessionIds: `{${trimmed}}`, ...dateRange },
      skip: skipSessionQuery,
      fetchPolicy: 'network-only',
    }
  );

  return useMemo(() => {
    if (!isValid) {
      return EMPTY;
    }

    // Cache hit — return immediately
    if (cacheResult) {
      return { result: cacheResult, isLoading: false };
    }

    // Session query in progress
    if (!skipSessionQuery && sessionLoading) {
      return { result: null, isLoading: true };
    }

    // Session found
    const session = sessionData?.rows?.[0];
    if (session) {
      const desksData = queryClient.getQueryData<Desks>([desksEndpoints.desks]);
      const desk = desksData?.desks?.find((d: Desk) => d.desk_id === session.desk_id);
      const isServiceDesk = desk?.is_service_desk ?? false;

      const path = isServiceDesk
        ? `/chats/${session.desk_id}/conversations/${session.session_id}?category=all`
        : `/analytics/account-chat-logs?session_id=${session.session_id}`;

      return {
        result: {
          id: session.session_id,
          name: session.user_name || session.channel_user_id || session.session_id,
          type: 'conversation' as const,
          path,
          icon: MessageSquareIcon,
          score: 1,
        },
        isLoading: false,
      };
    }

    return EMPTY;
  }, [isValid, cacheResult, skipSessionQuery, sessionLoading, sessionData, queryClient]);
};
